import {
    AsyncThunkOptions,
    createAsyncThunk,
    createSlice,
    PayloadAction,
} from "@reduxjs/toolkit"

import { itemsAPI, conceptsAPI } from "../../apis"
import { GlobalState } from "../../store"
import { Exceptions } from "../../util/Translations"

import { addErrorNotification } from "../notifications/actions"

import { IFlowPanel } from "."
import { IItemStatus, IStatusTransition } from "../../models"
import { IConcept } from "../itemdetail"

export interface Initialize {
    conceptId: number
    itemId: number
}

export const flowPanelSlice = createSlice({
    name: "flowPanel",

    initialState: {
        conceptId: -1,
        currentStatus: null,
        finalStatus: null,
        initialStatus: null,
        isVisible: false,
        itemId: -1,
        statusTransitions: [],
        statuses: [],
    } as IFlowPanel,

    reducers: {
        initialize: (state, action: PayloadAction<Initialize>) => {
            const { conceptId, itemId } = action.payload
            state.conceptId = conceptId
            state.itemId = itemId
        },
        setVisibility: (state, action: PayloadAction<boolean>) => {
            state.isVisible = action.payload
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchStatusTransitions.rejected, (state) => {
                state.statusTransitions = []
            })
            .addCase(fetchStatusTransitions.fulfilled, (state, action) => {
                state.statusTransitions = action.payload
            })
            .addCase(fetchConcept.rejected, (state) => {
                state.initialStatus = null
                state.finalStatus = null
                state.statuses = []
            })
            .addCase(fetchConcept.fulfilled, (state, action) => {
                state.initialStatus =
                    action.payload?.defaultInitialStatus ?? null
                state.finalStatus = action.payload?.defaultFinalStatus ?? null
                state.statuses = action.payload?.statuses ?? []
            })
            .addCase(fetchCurrentStatus.rejected, (state) => {
                state.currentStatus = null
            })
            .addCase(fetchCurrentStatus.fulfilled, (state, action) => {
                state.currentStatus = action.payload
            })
    },
})

export default flowPanelSlice.reducer

export const { initialize, setVisibility } = flowPanelSlice.actions

const thunkOptions: AsyncThunkOptions = {
    condition: (_, { getState }) => {
        const {
            flowPanel: { conceptId, itemId },
        } = getState() as GlobalState
        return conceptId > 0 && itemId > 0
    },
}

export const fetchStatusTransitions = createAsyncThunk(
    "flowPanel/fetchStatusTransitions",
    async (_, { dispatch, getState }) => {
        let result: IStatusTransition[] = []

        try {
            const {
                flowPanel: { conceptId },
            } = getState() as GlobalState
            result = await conceptsAPI.getStatusTransitions(conceptId)
        } catch (error) {
            console.error(error)
            dispatch(
                addErrorNotification(
                    Exceptions.ErrorFlowPanelFetchStatusTransitions,
                ),
            )
        }

        return result
    },
    thunkOptions,
)

export const fetchConcept = createAsyncThunk(
    "flowPanel/fetchConcept",
    async (_, { dispatch, getState }) => {
        let result: IConcept | null = null

        try {
            const {
                flowPanel: { conceptId },
            } = getState() as GlobalState
            result = await conceptsAPI.getConcept(conceptId)
        } catch (error) {
            console.error(error)
            dispatch(
                addErrorNotification(
                    Exceptions.ErrorFlowPanelFetchDefaultInitialStatus,
                ),
            )
        }

        return result
    },
    thunkOptions,
)

export const fetchCurrentStatus = createAsyncThunk(
    "flowPanel/fetchCurrentStatus",
    async (_, { dispatch, getState }) => {
        let result: IItemStatus | null = null

        try {
            const {
                flowPanel: { itemId },
            } = getState() as GlobalState
            result = await itemsAPI.getCurrentItemStatus(itemId)
        } catch (error) {
            console.error(error)
            dispatch(
                addErrorNotification(
                    Exceptions.ErrorFlowPanelFetchCurrentStatus,
                ),
            )
        }

        return result
    },
    thunkOptions,
)
